import { RootState } from '..';
import { DropdownItem } from '../../components/Input/InputDropdown';
import { getShowSingleForm } from '../form/getters';
import { defaultBannerForm } from '../form/utils';
import rootGetters from '../getters';
import { getUser } from '../user/getters';
import { getNestedField } from '../utils';

import * as lodash from 'lodash';

export const getBanner =
  (state: RootState) =>
  <T extends FlattenKeys<BannerState>>(field: T) =>
    getNestedField(state.banner, field);

export const getUnits = (state: RootState) => () => {
  const {
    banner: { getBannerConfig },
  } = rootGetters(state);
  const config = getBannerConfig('form-configurations')!.content as any;
  return config.units as Array<{ id: string; name: string }>;
};

export const getFormFragment =
  <T = DeepPartial<SingleBannerForm>>(state: RootState) =>
  (dynamic: keyof Dynamics): T => {
    const {
      banner: { getBannerConfig },
    } = rootGetters(state);
    const configurations = getBannerConfig('form-configurations')
      ?.content as any;
    return (
      configurations?.fragments?.find(({ dynamics: dy }) =>
        dy.includes(dynamic),
      )?.children ?? ({} as T)
    );
  };

export const getDynamics = (state: RootState) => () =>
  Object.entries(getBanner(state)('dynamics')).map(([id, name]) => ({
    id,
    name,
  })) as DropdownItem[];

export const getOrientations = (state: RootState) => () => {
  const selectedDynamic = getShowSingleForm(state)('model.dynamic');
  const selectedSize = getShowSingleForm(state)('model.size');
  const selectedLayouts = state.banner.configs.layouts.filter(
    ({ sizes, dynamics }) =>
      sizes.find(({ name }) => name === selectedSize) &&
      dynamics.find(({ name }) => name === selectedDynamic),
  );
  const selectedOrientations = selectedLayouts.map(
    ({ orientation }) => orientation,
  );
  return [...new Set(selectedOrientations)];
};

export const getOrientationItems = (state: RootState) => () =>
  Object.entries(getOrientations(state)()).map(([, name]) => ({
    id: name,
    name: state.banner.orientations[name],
  }));

export const getSizeItems = (state: RootState) => () => {
  const selectedDynamic = getShowSingleForm(state)('model.dynamic');

  const sizes = state.banner.configs.layouts
    .filter(({ dynamics }) =>
      dynamics.find(({ name }) => name === selectedDynamic),
    )
    .reduce((acc, acv) => {
      acv.sizes.forEach(({ name, height, width }) => {
        acc[name] = { width, height };
      });
      return acc;
    }, {}) as Record<SizeType, Size>;

  return Object.entries(sizes).map(([name, { height, width }]) => ({
    id: name,
    name: `${name} - ${width} x ${height} mm`,
  }));
};

export const getPrices = (state: RootState) => () => {
  const { price, promotion } = getShowSingleForm(state)('product.product');
  const prices: {
    id: number;
    name: number;
    type: string;
  }[] = [];

  prices.push({
    id: 0,
    name: price.regularPrice,
    type: 'Regular',
  });

  price.promotionalPrice !== 0 &&
    prices.push({
      id: 1,
      name: price.promotionalPrice,
      type: 'Promocão Geral',
    });

  promotion &&
    prices.push({
      id: 2,
      name: promotion.promotionalPrice,
      type: promotion.description,
    });

  return prices;
};

export const getPromotionalPrices = (state: RootState) => () => {
  const { price, promotion } = getShowSingleForm(state)('product.product');
  const prices: {
    id: number;
    name: number;
    type: string;
  }[] = [];

  price.promotionalPrice !== 0 &&
    prices.push({
      id: 4,
      name: price.promotionalPrice,
      type: 'Promocão Geral',
    });

  promotion &&
    prices.push({
      id: 5,
      name: promotion.promotionalPrice,
      type: promotion.description,
    });

  return prices;
};

export const getBackgroundImage =
  (state: RootState) => (form: SingleBannerForm) => {
    const {
      size: selectedSize,
      layout: selectedLayout,
      orientation: selectedOrientation,
      campaign: selectedCampaign,
      dynamic: selectedDynamic,
    } = form.model;

    const selectedFilename = state.banner.configs.layouts.find(
      ({ name, orientation, sizes, campaign, dynamics }) =>
        name === selectedLayout &&
        orientation === selectedOrientation &&
        campaign === selectedCampaign &&
        sizes.find(({ name }) => name === selectedSize) &&
        dynamics.find(({ name }) => name === selectedDynamic),
    )?.filename;

    return selectedFilename
      ? `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_BANNER_CAMPAIGN_URL}/${selectedFilename}`
      : null;
  };

export const getLayouts = (state: RootState) => () => {
  const selectedDynamic = getShowSingleForm(state)('model.dynamic');
  const selectedSize = getShowSingleForm(state)('model.size');
  const selectedOrientation = getShowSingleForm(state)('model.orientation');
  const selectedLayouts = state.banner.configs.layouts
    .filter(({ sizes }) => sizes.find(({ name }) => name === selectedSize))
    .filter(({ dynamics }) =>
      dynamics.find(({ name }) => name === selectedDynamic),
    )
    .filter(({ orientation }) => orientation == selectedOrientation)
    .map(({ name }) => name);
  return [...new Set(selectedLayouts)];
};

export const getLayoutItems = (state: RootState) => () =>
  Object.entries(getLayouts(state)()).map(([, name]) => ({
    id: name,
    name,
  }));

export const getCampaigns = (state: RootState) => () => {
  const {
    dynamic: selectedDynamic,
    size: selectedSize,
    layout: selectedLayout,
    orientation: selectedOrientation,
  } = getShowSingleForm(state)('model');
  const selectedCampaigns = state.banner.configs.layouts
    .filter(({ name }) => name === selectedLayout)
    .filter(({ sizes }) => sizes.find(({ name }) => name === selectedSize))
    .filter(({ orientation }) => orientation == selectedOrientation)
    .filter(({ dynamics }) =>
      dynamics.find(({ name }) => name === selectedDynamic),
    )
    .map(({ campaign }) => campaign);
  return [...new Set(selectedCampaigns)];
};

export const getCampaignItems = (state: RootState) => () =>
  Object.entries(getCampaigns(state)()).map(([, name]) => ({
    id: name,
    name,
  }));

export const getBannerId = (state: RootState) => () =>
  getUser(state)('current_user.clients.0.banner.id');

export const getBannerStatus =
  (state: RootState) => (status: keyof BannerState['status']) =>
    getBanner(state)('status')[status];

export const getBannerConfig =
  (state: RootState) =>
  (configName: BannerConfigName): BannerConfig | undefined => {
    switch (configName) {
      default:
        return state.banner.configs.configs.find(
          ({ name }) => name === configName,
        );
    }
  };

export const getPrintSettings = (state: RootState) => () => {
  const {
    banner: { getBannerConfig },
  } = rootGetters(state);
  return (getBannerConfig('print-settings')?.content ?? {
    defaultBackgroundImage: false,
  }) as PrintSettings;
};

export const getInterfaces = (state: RootState) => (group: InterfaceGroup) => {
  const {
    banner: { getBannerConfig },
  } = rootGetters(state);
  return (
    (getBannerConfig('node-configurations')?.content as any)?.interfaces ?? []
  ).filter(({ group: gp }) => group === gp) as Interface[];
};

export const getDefaultBannerForm =
  (state: RootState) => (): SingleBannerForm => {
    const {
      user: { getSelectedFilial },
      banner: { getBannerConfig },
    } = rootGetters(state);
    const filial = getSelectedFilial();
    const defaultClientFormConfig = getBannerConfig('default-form');
    const defaultLocalForm = defaultBannerForm();
    if (defaultClientFormConfig) {
      const defaultClientForm =
        defaultClientFormConfig.content as SingleBannerForm;
      return lodash.merge({}, defaultLocalForm, {
        ...defaultClientForm,
        price: {
          ...defaultClientForm.price,
          qrcode:
            defaultClientForm.price.qrcode ??
            filial.qrcode ??
            defaultLocalForm.price.qrcode,
        },
        product: {
          ...defaultClientForm.product,
          secondary:
            defaultClientForm.product.secondary ||
            defaultLocalForm.product.secondary,
        },
      } as SingleBannerForm);
    }
    return defaultLocalForm;
  };

export const getNodeConfigurations = (state: RootState) => () => {
  const {
    banner: { getBannerConfig },
  } = rootGetters(state);
  return getBannerConfig('node-configurations')!.content as any;
};

export const getAllLayouts = (state: RootState) => () =>
  state.banner.configs.layouts;

const bannerGetters = (state: RootState) => ({
  getBanner: getBanner(state),
  getLayoutItems: getLayoutItems(state),
  getDefaultBannerForm: getDefaultBannerForm(state),
  getOrientationItems: getOrientationItems(state),
  getUnits: getUnits(state),
  getDynamics: getDynamics(state),
  getSizeItems: getSizeItems(state),
  getBackgroundImage: getBackgroundImage(state),
  getPromotionalPrices: getPromotionalPrices(state),
  getPrices: getPrices(state),
  getCampaignItems: getCampaignItems(state),
  getBannerId: getBannerId(state),
  getBannerStatus: getBannerStatus(state),
  getBannerConfig: getBannerConfig(state),
  getNodeConfigurations: getNodeConfigurations(state),
  getPrintSettings: getPrintSettings(state),
  getAllLayouts: getAllLayouts(state),
  getInterfaces: getInterfaces(state),
  getFormFragment: getFormFragment(state),
});

export default bannerGetters;
